import { useEffect, useMemo, useState } from "react";
import { Pagination, usePageSize, usePaged } from "@/components/Pagination";
import { useStickySearch } from "@/lib/stickySearch";
import { Link, useNavigate } from "react-router-dom";
import { API, api, fetchAll, formatError } from "@/lib/apiClient";
import { StatusPill, pillLabel } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem } from "@/components/ui/dropdown-menu";
import {
  FilePlus, Download, Trash2, Edit3, ShoppingCart, MoreVertical,
  Sparkles, FileDown, Truck, Columns3, Copy, ChevronRight,
  Search, ArrowUp, ArrowDown, ArrowUpDown, MessageCircle, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { groupIcon, groupDesc, groupLink, groupIsOrderable, prettyType } from "@/lib/serviceGroups";
import { fmtDate, fmtTime } from "@/lib/dates";
import { useSiteConfig } from "@/lib/siteConfig";

// ============================================================================
// Table machinery
// ============================================================================
//
// **One column list drives four things**: the desktop header, every row, the mobile sort menu and
// the column chooser. They used to be written out separately per table, which is how Drafts ended
// up looking like a different product from Transactions and Orders — a plain stack of rows with no
// header, no sort and no columns, while the other two were grids. A column that exists is now a
// column all four surfaces know about, by construction.
//
// **There is no table below `md`.** A phone cannot show 800px of columns, so every `md:` here is
// deliberate: under that width a row becomes a card and each cell carries its own label. The grid
// tracks travel as CSS variables rather than Tailwind classes because the visible columns change
// at runtime — `md:grid-cols-[var(--tbl-cols)]` keeps the whole thing md-scoped anyway, so the
// variable never affects the card layout.

// Every row is the same height on desktop whatever it holds, so rows scan as a table rather than a
// stack of differently sized cards. 52px rather than 68px now that nothing in a row stacks onto a
// second line. Cards size to their content — a fixed floor there is wasted screen.
const ROW_H = "md:min-h-[52px]";

/**
 * One cell of a row, in both layouts.
 *
 * At `md`+ this is a plain grid item and `label` is not rendered — the header names the columns.
 * Below `md` the header is hidden and the row is a card, so the cell shows its own label beside
 * the value. Same label text in both, so nothing on a phone is a column you have to guess at.
 */
function Cell({ label, className = "", children }) {
  return (
    <div className={`flex items-start gap-3 md:block ${className}`}>
      {label && (
        <span className="md:hidden shrink-0 w-[76px] pt-0.5 text-[10px] tracking-[0.18em] uppercase text-[#6B7280]">
          {label}
        </span>
      )}
      <div className="min-w-0 flex-1 md:flex-none">{children}</div>
    </div>
  );
}

/**
 * Which columns the customer has switched off, remembered across visits.
 *
 * Stored as the hidden set rather than the visible one: a column added to the table later then
 * shows up by default instead of being silently absent for everyone who ever opened the chooser.
 */
function useHiddenColumns(storageKey, columns) {
  const fallback = () => columns.filter(c => c.hiddenByDefault).map(c => c.key);
  const [hidden, setHidden] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey));
      return new Set(Array.isArray(saved) ? saved : fallback());
    } catch {
      return new Set(fallback());
    }
  });
  const toggle = (key) => setHidden(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    try { localStorage.setItem(storageKey, JSON.stringify([...next])); } catch { /* private mode */ }
    return next;
  });
  return [hidden, toggle];
}

/**
 * A section with a heading, a search box, sort and column controls, and a table that becomes a
 * stack of labelled cards below `md`.
 */
function TableSection({
  eyebrow, title, testid, columns, prefsKey, rows, rowKey, onRowClick,
  searchPlaceholder, searchTestId, matches, defaultSort, emptyText,
}) {
  // Each section remembers its own query, keyed by testid — see lib/stickySearch.js.
  const [search, setSearch, clearSearch] = useStickySearch(testid);
  const [sortField, setSortField] = useState(defaultSort.field);
  const [sortDir, setSortDir] = useState(defaultSort.dir);
  const [hidden, toggleHidden] = useHiddenColumns(prefsKey, columns);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  // `hideable: false` wins over whatever is stored. A column can be *promoted* to mandatory after
  // someone has already switched it off, and it would otherwise stay invisible for them forever
  // with no control left in the chooser to bring it back.
  const shown = columns.filter(c => c.hideable === false || !hidden.has(c.key));
  const sortable = columns.filter(c => c.sort);
  // A hidden column can still be the sort field — hiding "Date" is a statement about screen space,
  // not about how the customer wants their list ordered.
  const activeSort = columns.find(c => c.key === sortField)?.sort;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? rows.filter(r => matches(r, q)) : rows.slice();
    if (!activeSort) return list;
    return list.sort((a, b) => {
      const av = activeSort(a), bv = activeSort(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, search, sortDir, activeSort, matches]);

  // Paginated, at the size admin sets in Settings.
  //
  // The dashboard stacks three of these — Drafts, In Progress, Orders — and each used to render
  // its whole list, so how far down the page "Orders" sat depended on how many documents the
  // customer happened to have. The more you had with us, the harder your own dashboard was to
  // read. Paging bounds every section to the same height whatever the customer's history.
  const pageSize = usePageSize();
  const paged = usePaged(visible, pageSize);
  useEffect(() => { paged.setPage(0); }, [search, sortField, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  // Grid tracks + a floor wide enough for them, both md-scoped through CSS variables.
  const gridVars = {
    "--tbl-cols": shown.map(c => c.width).join(" "),
    "--tbl-min": `${shown.reduce((n, c) => n + (parseInt(c.width, 10) || 180), 0)}px`,
  };

  return (
    // `scroll-mt` clears the fixed mobile header, which would otherwise sit on top of the heading
    // a stat card just jumped to.
    <section id={testid} data-testid={testid} className="scroll-mt-20 md:scroll-mt-6">
      <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#A0AABF]">{eyebrow}</div>
          <h2 className="font-serif-display text-2xl md:text-3xl mt-1">{title}</h2>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64 sm:flex-none">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <input
              data-testid={searchTestId} value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === "Escape") clearSearch(); }}
              placeholder={searchPlaceholder}
              className="w-full bg-transparent border border-white/15 rounded-full pl-9 pr-9 py-2 text-sm text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#CBA96C]/50"
            />
            {/* The only thing that forgets the query — navigation never does. Escape works too. */}
            {search && (
              <button type="button" onClick={clearSearch} data-testid={`${searchTestId}-clear`}
                      aria-label="Clear search"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#A0AABF] hover:text-white hover:bg-white/10 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {/* Sorting lives in the column headers, and a card layout has no headers — so below
              `md` it gets its own control rather than being unreachable. Same fields, same
              toggleSort, so the two layouts cannot drift apart. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`${testid}-sort`} aria-label={`Sort ${title}`}
                className="md:hidden shrink-0 flex items-center gap-1.5 border border-white/15 rounded-full px-3 py-2 text-xs text-[#A0AABF] hover:text-white transition-colors"
              >
                {sortDir === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
                {columns.find(c => c.key === sortField)?.label || "Sort"}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#111623] border-white/10 text-white">
              <DropdownMenuLabel className="text-[#6B7280] font-normal text-[10px] tracking-[0.18em] uppercase">Sort by</DropdownMenuLabel>
              {sortable.map(c => (
                <DropdownMenuItem
                  key={c.key} onClick={() => toggleSort(c.key)} data-testid={`${testid}-sort-${c.key}`}
                  className="cursor-pointer focus:bg-white/5 focus:text-white"
                >
                  <span className="flex-1">{c.label}</span>
                  {sortField === c.key && (
                    sortDir === "asc" ? <ArrowUp className="w-3 h-3 ml-2" /> : <ArrowDown className="w-3 h-3 ml-2" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Column chooser. Only the columns worth losing are offered — Name and the actions are
              what the row is for. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`${testid}-columns`} aria-label={`Choose columns for ${title}`}
                title="Choose columns"
                className="shrink-0 flex items-center gap-1.5 border border-white/15 rounded-full px-3 py-2 text-xs text-[#A0AABF] hover:text-white transition-colors"
              >
                <Columns3 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Columns</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#111623] border-white/10 text-white">
              <DropdownMenuLabel className="text-[#6B7280] font-normal text-[10px] tracking-[0.18em] uppercase">Show columns</DropdownMenuLabel>
              {columns.filter(c => c.hideable !== false).map(c => (
                <DropdownMenuCheckboxItem
                  key={c.key} checked={!hidden.has(c.key)} onCheckedChange={() => toggleHidden(c.key)}
                  data-testid={`${testid}-col-${c.key}`}
                  className="cursor-pointer focus:bg-white/5 focus:text-white"
                >
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="bg-[#111623] border border-white/10 rounded-2xl divide-y divide-white/5 overflow-x-auto" style={gridVars}>
        <Pagination {...paged} testid={`${testid}-pager`} />
        {rows.length > 0 && (
          <div className="hidden md:grid md:grid-cols-[var(--tbl-cols)] md:min-w-[var(--tbl-min)] gap-3 px-5 py-3 text-[10px] tracking-[0.24em] uppercase text-[#6B7280]">
            {shown.map(c => (
              c.sort ? (
                <button key={c.key} onClick={() => toggleSort(c.key)} data-testid={`sort-${c.key}`}
                  className={`flex items-center gap-1 hover:text-white transition-colors ${c.align === "right" ? "justify-end" : "text-left"}`}>
                  {c.label}
                  {sortField === c.key
                    ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
                    : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                </button>
              ) : (
                <div key={c.key} className={c.align === "right" ? "text-right" : ""}>{c.label}</div>
              )
            ))}
          </div>
        )}
        {rows.length === 0 && (
          <div className="p-10 text-center text-[#6B7280] text-sm">
            <Sparkles className="w-5 h-5 mx-auto mb-3 text-[#CBA96C]" strokeWidth={1.5} />
            {emptyText}
          </div>
        )}
        {rows.length > 0 && visible.length === 0 && (
          <div className="p-10 text-center text-[#6B7280] text-sm">Nothing matches "{search}".</div>
        )}
        {paged.visible.map(row => (
          <div
            key={rowKey(row)} data-testid={`${testid}-row-${rowKey(row)}`}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={`flex flex-col gap-1.5 md:grid md:grid-cols-[var(--tbl-cols)] md:min-w-[var(--tbl-min)] ${ROW_H} md:gap-3 px-5 py-4 md:items-start transition-colors ${onRowClick ? "cursor-pointer hover:bg-white/[0.02]" : ""}`}
          >
            {shown.map(c => (
              <Cell key={c.key} label={c.label || undefined} className={c.cellClass}>
                {c.render(row)}
              </Cell>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================================
// Shared cell renderers / labels
// ============================================================================

// Date and time on one line. Stacking them set the height of every row in every table for the
// sake of a 10px timestamp; inline costs about twenty pixels of column and buys back a line on
// each row, which is the better trade now that Date is a mandatory column. DD-MM-YYYY, pinned in
// @/lib/dates rather than left to the browser's locale, so it reads the same way as the printed
// document. `whitespace-nowrap` so the pair never wraps back into two lines at a narrow width.
const DateCell = ({ value }) => (
  <div className="flex items-baseline gap-1.5 whitespace-nowrap">
    <span>{fmtDate(value)}</span>
    <span className="text-[10px] text-[#6B7280]">{fmtTime(value)}</span>
  </div>
);

// What kind of document this is: the broad type — "Rental Agreement", "Affidavit" — not the
// specific template title. Fifteen distinct titles in a Type column is not a type, it is a second
// name; the template's own title stays reachable as the cell's tooltip.
const typeLabel = (t) => prettyType(t.service_type);
// The full template title, for the tooltip — "Affidavit for Change of Name" rather than just
// "Affidavit", for when the broad type isn't enough.
const typeDetail = (t) => t.template_title || typeLabel(t);
// What the *customer* calls it. Naming is optional, so the server supplies `display_name` — their
// name if they gave one, otherwise "Rental-08955309" (see auto_document_name). The type label is
// only reached for a payload predating that field; it is never a good answer, because three
// unnamed affidavits under it are three identical rows.
const docLabel = (t) => t.display_name || t.document_name || typeLabel(t);

const wizardLink = (t) =>
  `/customer/wizard/${t.service_type === "affidavit"
    ? `affidavit?type=${t.subtype}&txn=${t.id}`
    : `${t.service_type}?txn=${t.id}`}`;

const NameCell = ({ t }) => (
  <div className="min-w-0">
    <div className="truncate text-white" title={docLabel(t)}>{docLabel(t)}</div>
    {/* Only when the customer actually named it — otherwise this repeats the line above. */}
    {t.document_name && <div className="text-[10px] text-[#6B7280] truncate md:hidden">{typeDetail(t)}</div>}
  </div>
);

/**
 * The transaction id, with the two things a customer does with a case underneath it.
 *
 * Same shape as admin's `TxnIdCell`: the id identifies the case, and copying it or opening its
 * conversation are both *about* the case rather than being separate columns. Copy exists because
 * the eight characters on screen are not the id — support asks for the whole thing, and reading a
 * UUID off a phone screen by hand is how transcription errors happen.
 */
const TxnCell = ({ t, onOpen }) => {
  const nav = useNavigate();
  const copy = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(t.id); toast.success("Transaction ID copied"); }
    catch { toast.error("Could not copy — clipboard unavailable"); }
  };
  // Unread messages from DharmaTN (admin/reviewer) since this customer last opened the case —
  // see backend's _attach_customer_message_counts. Genuinely unread now, not a running total: it
  // used to fall back to `message_count` (every message ever, including the customer's own) when
  // there was nothing unread, which is why sending your own first message showed "1" here — a
  // badge for "you have a reply waiting" that lit up on your own words.
  const unread = t.reply_count || 0;
  return (
    // One line, at every width. Stacking the two 12px glyphs under the id (which is what admin
    // does) forced every row in the table two lines tall for the sake of a copy button — the
    // column is only a few pixels wider inline, and every row gets shorter.
    <div className="flex items-center gap-2.5 min-w-0">
      <div className="font-mono text-xs text-[#A0AABF] truncate">{t.id.slice(0, 8)}</div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          data-testid={`copy-txn-${t.id}`} onClick={copy}
          title={`Copy full ID — ${t.id}`} aria-label="Copy transaction ID"
          className="text-[#6B7280] hover:text-[#CBA96C] transition-colors"
        ><Copy className="w-3 h-3" /></button>
        {unread > 0 && (
          <button
            data-testid={`msg-count-${t.id}`}
            // Straight to the thread, not just the case — a click on "3 messages" that lands on
            // the top of the page and leaves the customer to scroll for them isn't what it promised.
            onClick={(e) => { e.stopPropagation(); nav(`/customer/txn/${t.id}#case-messages`); }}
            title={`${unread} unread message${unread === 1 ? "" : "s"} from DharmaTN`}
            className="inline-flex items-center gap-0.5 text-[10px] text-[#7DB2FB] hover:underline transition-colors"
          >
            <MessageCircle className="w-3 h-3" />{unread}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * The one thing an unpaid case is waiting on.
 *
 * These conditions overlap in the data but not in what the customer should do about them, so the
 * column shows exactly one and **missing details outrank everything**. An incomplete document
 * cannot be checked out — the server refuses it (`_reject_if_incomplete`) and the Checkout button
 * is disabled — so labelling a case that is both in the cart *and* unfinished "Payment Pending"
 * names the wrong party: it says we are waiting for money when we are waiting for their answers,
 * directly beside a button that will not work.
 *
 * Derived, not a tracked field — the same shape as `admin_stage` on the admin side.
 */
const draftState = (t) => {
  if ((t.incomplete_fields || []).length > 0) return { status: "details_missing" };
  if (t.payment_status === "failed") return { status: "failed", dim: "payment_status" };
  if (t.in_cart) return { status: "pending", dim: "status" };  // → "Payment Pending"
  return { status: "ready" };
};

const txnMatches = (t, q) => [
  t.id, t.document_name, typeLabel(t), typeDetail(t), t.service_type, t.subtype,
  t.status, pillLabel(t.status, "status"),
  t.payment_status, pillLabel(t.payment_status, "payment_status"),
  t.delivery_status, pillLabel(t.delivery_status, "delivery_status"),
].filter(Boolean).join(" ").toLowerCase().includes(q);

export default function CustomerDashboard() {
  const { user } = useAuth();
  const siteCfg = useSiteConfig();
  const nav = useNavigate();
  const [txns, setTxns] = useState([]);
  const [orders, setOrders] = useState([]);
  // Quick-start tiles come from the template groups, same as the New Document page — a hardcoded
  // list here meant a new group was unreachable from the dashboard.
  const [groups, setGroups] = useState([]);
  // null until loaded — an empty array would filter every tile away on first paint
  const [templates, setTemplates] = useState(null);

  const load = () => {
    fetchAll("/transactions/mine").then(setTxns).catch(() => {});
    fetchAll("/orders").then(setOrders).catch(() => {});
    api.get("/template-groups").then(r => setGroups(r.data)).catch(() => {});
    api.get("/templates").then(r => setTemplates(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // The dashboard splits every case into exactly one of three buckets, and **payment is the line**.
  //
  // Nothing happens on a case until it is paid for: no reviewer is assigned, admin does not see it
  // in a working queue (backend/LIFECYCLE.md). So an unpaid case — whether it is half-typed, sitting
  // in the cart at "Payment Pending", or a payment that failed — is still the customer's to finish,
  // which is what "draft" means to them. It used to be keyed off `status === "draft" && !in_cart`,
  // which sent cart items to Active even though nothing was active about them.
  //
  // The buckets are also exhaustive on purpose. Under the old rules a refunded case matched none of
  // them and vanished from the customer's dashboard entirely — 6 real transactions were invisible.
  // A refund is terminal (the money is back, the case is over), so it belongs with the finished
  // ones as a record rather than in a working list.
  const isFinished = (t) =>
    ["completed", "closed"].includes(t.status) ||
    t.delivery_status === "delivered" ||
    t.payment_status === "refunded";
  const isPaid = (t) => t.payment_status === "completed";

  const drafts = useMemo(() => txns.filter(t => !isFinished(t) && !isPaid(t)), [txns]);
  const active = useMemo(() => txns.filter(t => !isFinished(t) && isPaid(t)), [txns]);
  const fulfilled = useMemo(() => txns.filter(isFinished), [txns]);
  // Matches the Active Transactions table exactly, for the same reason `delivered` does — a stat
  // card that disagrees with the table beneath it is worse than no card.
  const inProgress = active.length;
  const delivered = fulfilled.length;

  const del = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    try { await api.delete(`/transactions/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e?.response?.data?.detail) || "Failed"); }
  };
  // Reports the server's reason rather than failing silently — the button is disabled for an
  // incomplete document, but a tab left open since before the last edit will still get the 400,
  // and "nothing happened" is the worst possible answer to that.
  const addCart = async (id) => {
    try { await api.post(`/cart/items/${id}`); toast.success("Added to cart"); load(); }
    catch (e) { toast.error(formatError(e?.response?.data?.detail) || "Could not add to cart"); load(); }
  };

  const tok = localStorage.getItem("dt_token");
  const first = (user?.name || "there").split(" ")[0];
  const stop = (e) => e.stopPropagation();
  // The server enforces this (require_draft_headroom); showing it is what stops the ceiling
  // arriving as a surprise refusal halfway through starting a sixth document.
  const maxDrafts = Number(siteCfg.max_drafts) || 5;
  const draftsFull = drafts.length >= maxDrafts;

  // Column lists are rebuilt each render rather than memoised: they close over `del`/`addCart`,
  // which close over `load`, so memoising correctly would mean a chain of useCallbacks to save
  // building three short arrays. TableSection memoises the part that actually costs anything
  // (filter + sort).
  //
  // ---- Drafts ----
  const draftColumns = [
    { key: "txn", label: "Txn", width: "134px", hideable: false, sort: t => t.id,
      cellClass: "min-w-0", render: t => <TxnCell t={t} onOpen={() => nav(`/customer/txn/${t.id}`)} /> },
    { key: "name", label: "Name", width: "minmax(0,1fr)", hideable: false, sort: t => docLabel(t).toLowerCase(),
      cellClass: "text-sm min-w-0", render: t => <NameCell t={t} /> },
    { key: "type", label: "Type", width: "160px", sort: t => typeLabel(t).toLowerCase(),
      cellClass: "text-xs text-[#A0AABF] min-w-0",
      render: t => <div className="truncate" title={typeDetail(t)}>{typeLabel(t)}</div> },
    { key: "edited", label: "Edited", width: "120px", hideable: false,
      sort: t => new Date(t.updated_at || t.created_at).getTime(),
      cellClass: "text-xs text-[#A0AABF] min-w-0",
      render: t => <DateCell value={t.updated_at || t.created_at} /> },
    // This list holds anything unpaid, so the column says which kind — see draftState for why it
    // is one pill and not several.
    { key: "state", label: "State", width: "150px", cellClass: "min-w-0",
      sort: t => pillLabel(draftState(t).status, draftState(t).dim),
      render: t => {
        const s = draftState(t);
        return <span data-testid={`draft-state-${t.id}`}><StatusPill status={s.status} dim={s.dim} /></span>;
      } },
    { key: "actions", label: "", width: "210px", hideable: false, align: "right", render: t => {
      const blocked = (t.incomplete_fields || []).length > 0;
      // One sentence, not a recital of field names — the wizard shows which answers are missing
      // in the place they can actually be filled in.
      const why = "Some required details are still missing — open it to finish";
      return (
        <div className="flex items-center gap-2 md:justify-end" onClick={stop}>
          <Link to={wizardLink(t)}>
            <Button size="sm" variant="outline" data-testid={`edit-draft-${t.id}`} className="bg-transparent border-white/15 text-white hover:bg-white/5 h-8 px-3">
              <Edit3 className="w-3 h-3 mr-1" /> Edit
            </Button>
          </Link>
          {t.in_cart ? (
            blocked ? (
              <Button size="sm" disabled data-testid={`draft-checkout-${t.id}`} title={why}
                className="bg-[#CBA96C] text-[#080C16] font-medium h-8 px-3 disabled:opacity-40">
                <ShoppingCart className="w-3 h-3 mr-1" /> Checkout
              </Button>
            ) : (
              <Link to="/customer/cart">
                <Button size="sm" data-testid={`draft-checkout-${t.id}`} className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] font-medium h-8 px-3">
                  <ShoppingCart className="w-3 h-3 mr-1" /> Checkout
                </Button>
              </Link>
            )
          ) : (
            <Button size="sm" disabled={blocked} onClick={() => addCart(t.id)} data-testid={`cart-add-${t.id}`}
              title={blocked ? why : undefined}
              className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] font-medium h-8 px-3 disabled:opacity-40">
              <ShoppingCart className="w-3 h-3 mr-1" /> Add
            </Button>
          )}
          <button onClick={() => del(t.id)} data-testid={`del-draft-${t.id}`} aria-label="Delete draft"
            className="p-2 rounded-full text-[#EF4444] hover:bg-white/5 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      );
    } },
  ];

  // ---- Active transactions ----
  const txnColumns = [
    // Counts only what this customer can actually read (see _attach_customer_message_counts).
    { key: "txn", label: "Txn", width: "134px", hideable: false, sort: t => t.id,
      cellClass: "min-w-0", render: t => <TxnCell t={t} onOpen={() => nav(`/customer/txn/${t.id}`)} /> },
    { key: "name", label: "Name", width: "minmax(0,1fr)", hideable: false, sort: t => docLabel(t).toLowerCase(),
      cellClass: "text-sm min-w-0", render: t => <NameCell t={t} /> },
    { key: "type", label: "Type", width: "160px", sort: t => typeLabel(t).toLowerCase(),
      cellClass: "text-xs text-[#A0AABF] min-w-0",
      render: t => <div className="truncate" title={typeDetail(t)}>{typeLabel(t)}</div> },
    { key: "date", label: "Date", width: "120px", hideable: false,
      sort: t => new Date(t.created_at).getTime(),
      cellClass: "text-xs text-[#A0AABF] min-w-0", render: t => <DateCell value={t.created_at} /> },
    { key: "status", label: "Status", width: "130px", hideable: false, sort: t => pillLabel(t.status, "status"),
      cellClass: "min-w-0", render: t => <StatusPill status={t.status} dim="status" /> },
    { key: "payment", label: "Payment", width: "130px", sort: t => pillLabel(t.payment_status, "payment_status"),
      cellClass: "min-w-0", render: t => <StatusPill status={t.payment_status} dim="payment_status" /> },
    { key: "delivery", label: "Delivery", width: "170px", sort: t => pillLabel(t.delivery_status, "delivery_status"),
      cellClass: "min-w-0", render: t => (
      <div className="flex items-center gap-1">
        <StatusPill status={t.delivery_status} dim="delivery_status" />
        {(t.tracking_url || t.courier_name || t.tracking_id) && (
          <div onClick={stop}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid={`delivery-menu-${t.id}`} aria-label="Tracking details"
                  className="p-1 rounded-full text-[#A0AABF] hover:text-white hover:bg-white/5 transition-colors">
                  <MoreVertical className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-[#111623] border-white/10 text-white">
                {(t.courier_name || t.tracking_id) && (
                  <DropdownMenuLabel className="text-[#A0AABF] font-normal text-xs">
                    {t.courier_name}{t.courier_name && t.tracking_id ? " · " : ""}{t.tracking_id}
                  </DropdownMenuLabel>
                )}
                {t.tracking_url && (
                  <DropdownMenuItem asChild className="cursor-pointer focus:bg-white/5 focus:text-white">
                    <a href={t.tracking_url} target="_blank" rel="noopener noreferrer" data-testid={`track-${t.id}`}>
                      <Truck className="w-3.5 h-3.5 mr-2" /> Track shipment
                    </a>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    ) },
    { key: "actions", label: "", width: "44px", hideable: false, align: "right", render: t => {
      const sc = (t.reports || []).find(r => r.kind === "softcopy") || (t.reports || [])[0];
      const paymentLocked = ["completed", "refunded"].includes(t.payment_status);
      const editable = !paymentLocked && (t.service_type === "rental_agreement" || t.service_type === "affidavit");
      if (!(editable || sc || t.order_id || !paymentLocked)) return null;
      return (
        <div className="flex items-center md:justify-end" onClick={stop}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`txn-menu-${t.id}`} aria-label="More actions"
                className="p-2 rounded-full text-[#A0AABF] hover:text-white hover:bg-white/5 transition-colors">
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#111623] border-white/10 text-white">
              {editable && (
                <DropdownMenuItem asChild className="cursor-pointer focus:bg-white/5 focus:text-white">
                  <Link to={wizardLink(t)} data-testid={`edit-txn-${t.id}`}>
                    <Edit3 className="w-3.5 h-3.5 mr-2" /> Edit
                  </Link>
                </DropdownMenuItem>
              )}
              {sc && (
                <DropdownMenuItem asChild className="cursor-pointer focus:bg-white/5 focus:text-white">
                  <a href={`${API}/files/${sc.id}?_t=${tok}`} target="_blank" rel="noopener noreferrer" data-testid={`dl-soft-${t.id}`}>
                    <FileDown className="w-3.5 h-3.5 mr-2" /> Soft Copy
                  </a>
                </DropdownMenuItem>
              )}
              {t.order_id && (
                <DropdownMenuItem asChild className="cursor-pointer focus:bg-white/5 focus:text-white">
                  <a href={`${API}/orders/${t.order_id}/bill?_t=${tok}`} target="_blank" rel="noopener noreferrer" data-testid={`dl-bill-${t.id}`}>
                    <Download className="w-3.5 h-3.5 mr-2" /> Download bill
                  </a>
                </DropdownMenuItem>
              )}
              {!paymentLocked && (editable || sc || t.order_id) && <DropdownMenuSeparator className="bg-white/10" />}
              {!paymentLocked && (
                <DropdownMenuItem onClick={() => del(t.id)} data-testid={`del-txn-${t.id}`}
                  className="cursor-pointer text-[#EF4444] focus:bg-red-500/10 focus:text-[#EF4444]">
                  <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    } },
  ];

  // ---- Orders ----
  const orderColumns = [
    { key: "txn", label: "Txn", width: "134px", hideable: false, sort: t => t.id,
      cellClass: "min-w-0", render: t => <TxnCell t={t} onOpen={() => nav(`/customer/txn/${t.id}`)} /> },
    { key: "name", label: "Name", width: "minmax(0,1fr)", hideable: false, sort: t => docLabel(t).toLowerCase(),
      cellClass: "text-sm min-w-0", render: t => <NameCell t={t} /> },
    { key: "type", label: "Type", width: "160px", sort: t => typeLabel(t).toLowerCase(),
      cellClass: "text-xs text-[#A0AABF] min-w-0",
      render: t => <div className="truncate" title={typeDetail(t)}>{typeLabel(t)}</div> },
    { key: "completed", label: "Completed", width: "120px", hideable: false,
      sort: t => new Date(t.updated_at || t.created_at).getTime(),
      cellClass: "text-xs text-[#A0AABF] min-w-0",
      render: t => <DateCell value={t.updated_at || t.created_at} /> },
    { key: "delivery", label: "Delivery", width: "150px", hideable: false,
      sort: t => pillLabel(t.delivery_status, "delivery_status"),
      cellClass: "min-w-0", render: t => <StatusPill status={t.delivery_status} dim="delivery_status" /> },
    { key: "amount", label: "Amount", width: "110px", sort: t => Number(t.price || 0),
      cellClass: "text-sm text-white min-w-0",
      render: t => <div className="truncate">₹{Number(t.price || 0).toFixed(0)}</div> },
    { key: "documents", label: "Documents", width: "110px", hideable: false, align: "right", render: t => {
      const sc = (t.reports || []).find(r => r.kind === "softcopy") || (t.reports || [])[0];
      return (
        <div className="flex items-center gap-3 md:justify-end" onClick={stop}>
          {sc && (
            <a href={`${API}/files/${sc.id}?_t=${tok}`} target="_blank" rel="noopener noreferrer"
              data-testid={`order-soft-${t.id}`} title="Soft copy" className="text-[#CBA96C] hover:opacity-80">
              <FileDown className="w-4 h-4" />
            </a>
          )}
          {t.order_id && (
            <a href={`${API}/orders/${t.order_id}/bill?_t=${tok}`} target="_blank" rel="noopener noreferrer"
              data-testid={`order-bill-${t.id}`} title="Bill" className="text-[#A0AABF] hover:text-white">
              <Download className="w-4 h-4" />
            </a>
          )}
          {!sc && !t.order_id && <span className="text-[10px] text-[#6B7280]">—</span>}
        </div>
      );
    } },
  ];

  // space-y-7, not space-y-12.
  // This page's job is to answer "where are my documents?", and In Progress and Orders are the
  // answer. They used to start at 667px and 892px in a 900px viewport, so a returning customer had
  // to scroll past a greeting and a template picker to reach the reason they signed in. The
  // greeting and quick-start are still here, sized as the secondary things they are.
  return (
    <div className="space-y-7" data-testid="customer-dashboard">
      {/* Hero greeting */}
      <div>
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#CBA96C] mb-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#CBA96C] opacity-50" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#CBA96C]" />
          </span>
          Your workspace
        </div>
        <h1 className="font-serif-display text-2xl md:text-3xl tracking-tight leading-tight">
          Welcome back, <span className="italic text-[#CBA96C]">{first}</span>.
        </h1>
        {/* One line, not two. The old copy explained what the page is for, which is worth saying to
            someone on their first visit and is dead weight on every visit after — and it cost two
            lines of the fold that the tables below need more. */}
        <p className="mt-1.5 text-sm text-[#A0AABF]">Pick up a draft, start a new document, or track a delivery.</p>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* In lifecycle order, and in the same order as the tables below: a draft becomes work in
            progress, becomes an order, arrives. The strip used to read Drafts / In Progress /
            Delivered / Orders, which stepped backwards at the end. */}
        {/* Each card jumps to the table it counted. "Delivered" points at Orders too — delivered
            cases are a subset of that table, not a table of their own. */}
        <StatCard label="Drafts" value={drafts.length} accent="#A0AABF" limit={maxDrafts} targetId={drafts.length ? "drafts" : null} />
        <StatCard label="In Progress" value={inProgress} accent="#F59E0B" targetId="txn" />
        <StatCard label="Orders" value={orders.length} accent="#CBA96C" targetId={fulfilled.length ? "orders" : null} />
        <StatCard label="Delivered" value={delivered} accent="#10B981" targetId={fulfilled.length ? "orders" : null} />
      </div>

      {/* Quick start */}
      <section>
        <div className="flex items-end justify-between mb-3 gap-4 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#A0AABF]">Quick start</div>
            <h2 className="font-serif-display text-2xl md:text-3xl mt-1">Start a new document</h2>
          </div>
          <Link to="/customer/new">
            <Button data-testid="dash-new-btn" variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/5 rounded-full h-10 px-5">
              <FilePlus className="w-3.5 h-3.5 mr-1.5" /> All templates
            </Button>
          </Link>
        </div>
        {/* Said here rather than only on the refusal: the customer is about to pick a template,
            and finding out three questions in that they cannot save it is the worse moment. */}
        {draftsFull && (
          <div data-testid="drafts-full-notice" className="mb-3 rounded-xl border border-[#F27373]/40 bg-[#F27373]/5 px-4 py-3 text-xs text-[#F27373]">
            You have {drafts.length} unfinished documents, which is the maximum. Finish, pay for or
            delete one before starting another.
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {groups.filter(g => templates === null || groupIsOrderable(g.key, templates)).map(g => {
            const Icon = groupIcon(g);
            return (
              <Link key={g.key} to={groupLink(g.key, templates)} data-testid={`quick-${g.key}`}
                className="group rounded-2xl border border-white/10 bg-[#111623] p-4 hover:border-[#CBA96C]/40 transition-colors">
                <Icon className="w-5 h-5 text-[#CBA96C]" strokeWidth={1.5}/>
                <div className="text-sm text-white mt-2.5">{g.label}</div>
                <div className="text-xs text-[#6B7280] mt-1 leading-snug">{groupDesc(g)}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {drafts.length > 0 && (
        <TableSection
          eyebrow="Continue where you left off" title="Drafts" testid="drafts"
          columns={draftColumns} prefsKey="dharmatn.cols.drafts" rows={drafts}
          rowKey={t => t.id}
          searchTestId="draft-search" searchPlaceholder="Search name, type…" matches={txnMatches}
          defaultSort={{ field: "edited", dir: "desc" }}
          emptyText="No drafts."
        />
      )}

      {/* "In Progress", not "Transactions": every row on this page is a transaction, so the word
          named nothing. These are the paid cases actually being worked on, which is what the
          matching stat card has always called them. */}
      <TableSection
        eyebrow="Active" title="In Progress" testid="txn"
        columns={txnColumns} prefsKey="dharmatn.cols.txns" rows={active}
        rowKey={t => t.id} onRowClick={t => nav(`/customer/txn/${t.id}`)}
        searchTestId="txn-search" searchPlaceholder="Search name, type, status…" matches={txnMatches}
        defaultSort={{ field: "date", dir: "desc" }}
        emptyText="No active transactions yet. Start with a template above."
      />

      {fulfilled.length > 0 && (
        /* Deliberately a plainer table than Transactions: this is a record of what was delivered,
           not a working queue, so it carries no edit/delete actions — only the document, the bill,
           and a way back into the case. */
        <TableSection
          eyebrow="Completed" title="Orders" testid="orders"
          columns={orderColumns} prefsKey="dharmatn.cols.orders" rows={fulfilled}
          rowKey={t => t.id} onRowClick={t => nav(`/customer/txn/${t.id}`)}
          searchTestId="order-search" searchPlaceholder="Search name, type, delivery…" matches={txnMatches}
          defaultSort={{ field: "completed", dir: "desc" }}
          emptyText="No completed orders yet."
        />
      )}
    </div>
  );
}

/**
 * A count, and a way to get to the rows it counted.
 *
 * The number was previously inert — it told the customer they had four drafts and then left them
 * to scroll for the table. `targetId` turns the card into a jump link; a card whose table is not
 * on the page (nothing in that bucket, so the section is not rendered) stays a plain tile rather
 * than a control that does nothing.
 */
function StatCard({ label, value, accent, targetId, limit }) {
  // `limit` turns the number into a quota — "3/5" — so the ceiling is visible before the customer
  // hits it, rather than arriving as a refusal when they try to start a sixth document.
  const atLimit = limit != null && value >= limit;
  const body = (
    <>
      <div className="text-[10px] tracking-[0.24em] uppercase text-[#6B7280] flex items-center gap-1">
        {label}
        {targetId && <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />}
      </div>
      <div className="font-serif-display text-3xl mt-2 flex items-baseline gap-1" style={{ color: atLimit ? "#F27373" : accent }}>
        {value}
        {limit != null && <span className="text-base text-[#6B7280]">/{limit}</span>}
      </div>
    </>
  );
  const cls = "bg-[#111623] border border-white/10 rounded-2xl p-5 text-left w-full";
  if (!targetId) return <div className={cls}>{body}</div>;
  return (
    <button
      type="button" data-testid={`stat-${targetId}`}
      onClick={() => document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "start" })}
      className={`${cls} group hover:border-[#CBA96C]/40 transition-colors cursor-pointer`}
    >{body}</button>
  );
}
